import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, EnvelopeSimple, MapPin, Buildings, PaperPlaneTilt, CheckCircle } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import { CONTROLLER, LEGAL_LINKS } from "../lib/legal";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

const REQUEST_TYPES = [
    { value: "assistenza", label: "Assistenza generale" },
    { value: "accesso", label: "Accesso ai miei dati (art. 15)" },
    { value: "rettifica", label: "Rettifica dei dati (art. 16)" },
    { value: "cancellazione", label: "Cancellazione / Oblio (art. 17)" },
    { value: "portabilita", label: "Portabilità dei dati (art. 20)" },
    { value: "opposizione", label: "Opposizione al trattamento (art. 21)" },
    { value: "limitazione", label: "Limitazione del trattamento (art. 18)" },
    { value: "altro", label: "Altro" },
];

export default function Contact() {
    const [form, setForm] = useState({ name: "", email: "", request_type: "assistenza", message: "", website: "" });
    const [accept, setAccept] = useState(false);
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!accept) { toast.error("Accetta l'informativa privacy per inviare"); return; }
        setLoading(true);
        try {
            await api.post("/contact", { ...form, accept_privacy: true });
            setSent(true);
            toast.success("Richiesta inviata");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-white text-slate-100" data-testid="contact-page">
            <header className="border-b border-gray-200 sticky top-0 bg-white/90 backdrop-blur z-10">
                <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
                    <Link to="/" className="flex items-center" data-testid="contact-home-link">
                        <img src="/logo-full.png" alt="Asta Intelligence" className="h-24 w-auto mix-blend-screen" />
                    </Link>
                    <Link to="/" className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-black transition-colors">
                        <ArrowLeft size={14} weight="bold" /> Torna alla home
                    </Link>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-6 py-12">
                <div className="overline mb-2">Contatti &amp; Assistenza</div>
                <h1 className="font-display-black text-4xl sm:text-5xl mb-2 leading-[0.95]">Parliamone.</h1>
                <p className="text-sm text-gray-600 mb-10 max-w-xl">
                    Per assistenza o per esercitare i tuoi diritti sulla privacy (GDPR), compila il modulo
                    o scrivici direttamente via PEC. Rispondiamo nei termini di legge.
                </p>

                <div className="grid md:grid-cols-[1fr_1.3fr] gap-8">
                    {/* Contact info */}
                    <div className="space-y-5">
                        <div className="flex items-start gap-3">
                            <Buildings size={18} weight="fill" className="text-gray-700 mt-0.5 shrink-0" />
                            <div className="text-sm">
                                <div className="font-bold">{CONTROLLER.name}</div>
                                <div className="text-gray-500">P.IVA / C.F. {CONTROLLER.vat} · REA {CONTROLLER.rea}</div>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <MapPin size={18} weight="fill" className="text-gray-700 mt-0.5 shrink-0" />
                            <div className="text-sm text-gray-700">{CONTROLLER.address}</div>
                        </div>
                        <div className="flex items-start gap-3">
                            <EnvelopeSimple size={18} weight="fill" className="text-gray-700 mt-0.5 shrink-0" />
                            <div className="text-sm">
                                <div className="overline mb-0.5">PEC</div>
                                <a href={`mailto:${CONTROLLER.pec}`} data-testid="contact-pec-link" className="font-semibold underline underline-offset-4 hover:text-black break-all">
                                    {CONTROLLER.pec}
                                </a>
                            </div>
                        </div>
                        <div className="border-t border-gray-100 pt-4 text-xs text-gray-500 leading-relaxed">
                            Puoi anche presentare reclamo al Garante per la protezione dei dati personali
                            (www.garanteprivacy.it).
                        </div>
                    </div>

                    {/* Form */}
                    {sent ? (
                        <div data-testid="contact-success" className="border border-emerald-200 bg-emerald-500/[0.07] p-6 flex flex-col items-start gap-3">
                            <CheckCircle size={28} weight="fill" className="text-emerald-600" />
                            <h2 className="font-display-black text-xl">Richiesta inviata</h2>
                            <p className="text-sm text-gray-700">
                                Grazie. Abbiamo registrato la tua richiesta e ti risponderemo all&apos;indirizzo
                                indicato nei termini previsti dalla normativa. Per urgenze puoi scrivere a{" "}
                                <a href={`mailto:${CONTROLLER.pec}`} className="underline font-semibold">{CONTROLLER.pec}</a>.
                            </p>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="space-y-4 bg-white border border-gray-200 p-6" data-testid="contact-form">
                            {/* honeypot: hidden from users, bots tend to fill it */}
                            <input
                                type="text"
                                tabIndex={-1}
                                autoComplete="off"
                                value={form.website}
                                onChange={update("website")}
                                aria-hidden="true"
                                style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
                            />
                            <div>
                                <label className="overline block mb-2">Nome e cognome</label>
                                <Input required value={form.name} onChange={update("name")} placeholder="Mario Rossi" data-testid="contact-name" className="h-11" />
                            </div>
                            <div>
                                <label className="overline block mb-2">Email</label>
                                <Input required type="email" value={form.email} onChange={update("email")} placeholder="mario@studio.it" data-testid="contact-email" className="h-11" />
                            </div>
                            <div>
                                <label className="overline block mb-2">Tipo di richiesta</label>
                                <Select value={form.request_type} onValueChange={(v) => setForm({ ...form, request_type: v })}>
                                    <SelectTrigger className="h-11" data-testid="contact-type"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {REQUEST_TYPES.map((t) => (
                                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="overline block mb-2">Messaggio</label>
                                <Textarea required minLength={5} rows={5} value={form.message} onChange={update("message")} placeholder="Descrivi la tua richiesta…" data-testid="contact-message" />
                            </div>
                            <label className="flex items-start gap-2 text-xs text-gray-600 cursor-pointer">
                                <input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} data-testid="contact-accept-privacy" className="mt-0.5 accent-black w-4 h-4 shrink-0" />
                                <span>
                                    Ho letto l&apos;<Link to="/privacy" target="_blank" className="font-semibold underline underline-offset-2 hover:text-black">informativa privacy</Link>{" "}
                                    e acconsento al trattamento dei dati per gestire la mia richiesta.
                                </span>
                            </label>
                            <button
                                type="submit"
                                disabled={loading || !accept}
                                data-testid="contact-submit"
                                className="w-full h-11 bg-black text-white text-sm font-semibold hover:bg-gray-800 disabled:opacity-40 inline-flex items-center justify-center gap-2 transition-colors"
                            >
                                <PaperPlaneTilt size={16} weight="bold" /> {loading ? "Invio…" : "Invia richiesta"}
                            </button>
                        </form>
                    )}
                </div>

                <div className="mt-12 pt-8 border-t border-gray-200">
                    <nav className="flex flex-wrap gap-x-5 gap-y-2 text-xs">
                        {LEGAL_LINKS.map((l) => (
                            <Link key={l.to} to={l.to} className="font-semibold text-gray-500 hover:text-black underline underline-offset-4">{l.label}</Link>
                        ))}
                    </nav>
                </div>
            </main>
        </div>
    );
}
